import { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../../context/AppContext';
import { Card, Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Wallet } from 'lucide-react';
import CurrencyDisplay from '../../../component/wallet/CurrencyDisplay';
import LoadingSpinner from '../../../component/wallet/LoadingSpinner';
import { useWallets } from '../../../Hooks/wallet/use-wallets';
import { useAuth } from '../../../context/wallet/WalletAuthContext';
import { getUsers } from '../../../api/wallet/wallet.api';

const transferFormSchema = z.object({
    to_user_id: z.coerce.number().int().positive('Please select a recipient'),
    amount: z.coerce.number().positive('Amount must be a positive number'),
    description: z.string().optional().or(z.literal('')),
});

const TransferPage = () => {
    const { sidebar } = useContext(AppContext);
    const { loading, doTransfer, fetchWallets } = useWallets();
    const { userId } = useAuth();
    const [wallet, setWallet] = useState(null);
    const [walletLoading, setWalletLoading] = useState(true);
    const [users, setUsers] = useState([]);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({ resolver: zodResolver(transferFormSchema) });

    useEffect(() => {
        const load = async () => {
            try {
                const [walletRes, usersRes] = await Promise.all([
                    fetchWallets(),
                    getUsers({ limit: 99999 }),
                ]);
                const wallets = walletRes?.data || [];
                if (wallets.length > 0) setWallet(wallets[0]);
                const allUsers = usersRes?.data?.users || [];
                setUsers(allUsers.filter((u) => String(u.id) !== String(userId)));
            } catch {
                // handled
            } finally {
                setWalletLoading(false);
            }
        };
        load();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshWallet = async () => {
        try {
            const res = await fetchWallets();
            const wallets = res?.data || [];
            if (wallets.length > 0) setWallet(wallets[0]);
        } catch {
            // handled
        }
    };

    const onSubmit = async (data) => {
        if (!wallet) return;
        try {
            await doTransfer({ ...data, currency_code: wallet.currency_code });
            reset();
            await refreshWallet();
        } catch {
            // handled
        }
    };

    if (walletLoading) return <LoadingSpinner fullPage />;

    return (
        <div className={sidebar ? "taskMain " : "cx-active taskMain"} id="cx-main">
            <div className="role-content-main">
            <div className="sub-header-role mb-4">
                <h6 className="mt-2 role-text">Transfer Funds</h6>
            </div>
                <div className="fade-in">
            

            {/* Wallet Info */}
            {wallet && (
                <Card className="border-0 shadow-sm mb-4" style={{ Width: "100%" }}>
                    <Card.Body>
                        <div className="d-flex align-items-center gap-2 mb-3 text-muted small fw-semibold text-uppercase">
                            <Wallet size={16} />
                            {wallet.currency_code} Wallet
                        </div>
                        <Row className="g-3">
                            <Col xs={6}>
                                <div className="text-muted small">Balance</div>
                                <CurrencyDisplay
                                    amount={wallet.balance}
                                    currencySymbol={wallet.currency_symbol}
                                    className="fw-semibold"
                                />
                            </Col>
                            <Col xs={6}>
                                <div className="text-muted small">Available</div>
                                <CurrencyDisplay
                                    amount={wallet.available_balance}
                                    currencySymbol={wallet.currency_symbol}
                                    className="fw-semibold"
                                />
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {/* Transfer Form */}
            <Card className="border-0 shadow-sm" style={{ Width: "100%" }}>
                <Card.Body className="p-4">
                    {!wallet ? (
                        <p className="text-muted mb-0">No wallet found. Please contact support.</p>
                    ) : (
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <Row>
                               <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Recipient</Form.Label>
                                <Form.Select
                                    isInvalid={!!errors.to_user_id}
                                    {...register('to_user_id')}
                                >
                                    <option value="">Select recipient</option>
                                    {users.map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.full_name || u.email || `User #${u.id}`}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{errors.to_user_id?.message}</Form.Control.Feedback>
                            </Form.Group>
                            </Col>
                         

                              <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Amount ({wallet.currency_symbol}{wallet.currency_code})</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    placeholder="0.00"
                                    isInvalid={!!errors.amount}
                                    {...register('amount')}
                                />
                                <Form.Control.Feedback type="invalid">{errors.amount?.message}</Form.Control.Feedback>
                            </Form.Group>
                            </Col>
                          
                            <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Description <span className="text-muted fw-normal">(optional)</span></Form.Label>
                                <Form.Control type="text" placeholder="e.g., Transfer to friend" {...register('description')} />
                            </Form.Group>
                            </Col>
                            </Row>

                            <Button type="submit" variant="primary" disabled={loading} className="w-20">
                                {loading ? <Spinner size="sm" /> : `Transfer ${wallet.currency_symbol}${wallet.currency_code}`}
                            </Button>
                        </Form>
                    )}
                </Card.Body>
            </Card>
        </div>
        </div>
        </div>
    );
};

export default TransferPage;
